export type CatalogState = { q: string; minPrice: string; maxPrice: string; sort: "newest" | "catalog" | "price-asc" | "price-desc" | "popularity" | "rating"; page: number; attributes?: Record<string, string | string[]> };
export const CATALOG_PAGE_SIZE = 12;
export const DEFAULT_CATALOG_STATE: CatalogState = { q: "", minPrice: "", maxPrice: "", sort: "newest", page: 1 };

export function catalogPriceMinor(value: string): number | undefined {
  if (!/^\d{1,7}(?:[.,]\d{1,2})?$/.test(value)) return undefined;
  const result = Math.round(Number(value.replace(",", ".")) * 100);
  return Number.isSafeInteger(result) ? result : undefined;
}
export function parseCatalogState(params: URLSearchParams): CatalogState {
  const price = (name: string) => {
    const minor = catalogPriceMinor(params.get(name) || "");
    return minor === undefined ? "" : (minor / 100).toFixed(2);
  };
  const sort = params.get("sort");
  const attributes: Record<string, string | string[]> = {};
  for (const key of [...new Set(params.keys())].filter((key) => key.startsWith("attr.") && key.length > 5 && key.length <= 165).slice(0, 20)) {
    const values = [...new Set(params.getAll(key).filter((value) => value.length > 0 && value.length <= 160))].slice(0, 20);
    if (values.length) attributes[key.slice(5)] = values.length === 1 ? values[0] : values;
  }
  // Accept URLs created by the earlier feature preview as aliases.
  for (const key of [...new Set(params.keys())].filter((key) => /^attribute\[[^\]]{1,80}\]$/.test(key)).slice(0, 8)) {
    const name = key.slice(10, -1); const value = params.get(key) || "";
    if (!(name in attributes) && value && value.length <= 160) attributes[name] = value;
  }
  return {
    q: (params.get("q") || "").trim().slice(0, 120),
    minPrice: price("minPrice"), maxPrice: price("maxPrice"),
    sort: sort === "price-asc" || sort === "price-desc" || sort === "catalog" || sort === "popularity" || sort === "rating" ? sort : "newest",
    page: Math.min(834, Math.max(1, Math.trunc(Number(params.get("page")) || 1))),
    ...(Object.keys(attributes).length ? { attributes } : {}),
  };
}
export function catalogSearch(state: CatalogState, existing = ""): string {
  const params = new URLSearchParams(existing);
  for (const key of [...params.keys()]) if (key.startsWith("attr.") || key.startsWith("attribute[")) params.delete(key);
  for (const [name, value] of Object.entries(state.attributes ?? {})) for (const entry of Array.isArray(value) ? value : [value]) if (entry) params.append(`attr.${name}`, entry);
  for (const [key, value] of Object.entries(state)) {
    if (key === "attributes") continue;
    if (value === DEFAULT_CATALOG_STATE[key as keyof CatalogState] || value === "") params.delete(key);
    else params.set(key, String(value));
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}
export function catalogStateKey(state: CatalogState) { return JSON.stringify(state); }

/** Restore the seed result, or discard a fetch that completed after cancellation. */
export async function loadCatalogState<T>(state: CatalogState, initialState: CatalogState, initialData: T | undefined,
  signal: AbortSignal, fetchData: () => Promise<T>): Promise<T | undefined> {
  if (signal.aborted) return undefined;
  if (initialData !== undefined && catalogStateKey(state) === catalogStateKey(initialState)) return initialData;
  const result = await fetchData();
  return signal.aborted ? undefined : result;
}
